"use client";

import * as React from "react";
import Image from "next/image";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    FormControl,
    Grid,
    InputAdornment,
    InputBase,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CarInvestmentCard, { CarInvestmentCardProps } from "./PickYourPrivateJetCard";
import Heading from "../heading";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import IconButton from "@mui/material/IconButton";
import EnquireFormModal from '../../components/all-services/enquiryForm'
import CtaButton from "../buttonWithArrow";
import AddIcon from '@mui/icons-material/Add';

const INITIAL_STATE = {
    vehicle: '',
    initial: '',
    monthly: '',
    month: ''
}

function JetCardSkeleton() {
    return (
        <Card
            sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                overflow: "hidden",
            }}
        >
            {/* Badge */}
            <Box sx={{ p: 1 }}>
                <Skeleton variant="rounded" width={120} height={24} />
            </Box>

            {/* Image */}
            <Skeleton
                variant="rectangular"
                height={180}
                width="100%"
                animation="wave"
            />

            <CardContent>
                {/* Title */}
                <Typography align="center" sx={{ my: 1 }}>
                    <Skeleton variant="text" width="60%" height={28} sx={{ mx: "auto" }} />
                </Typography>

                {/* Shares Info */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    {["Total Shares", "Sold Shares", "Available Shares"].map((_, i) => (
                        <Grid item xs={4} key={i}>
                            <Box
                                sx={{
                                    border: "1px solid #eee",
                                    borderRadius: 2,
                                    p: 1,
                                    textAlign: "center",
                                }}
                            >
                                <Skeleton variant="text" width="70%" height={18} sx={{ mx: "auto" }} />
                                <Skeleton variant="text" width="40%" height={18} sx={{ mx: "auto" }} />
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Price Info */}
                <Box sx={{ mb: 2 }}>
                    <Skeleton
                        variant="rounded"
                        height={40}
                        width="100%"
                        sx={{ mb: 1, borderRadius: 2 }}
                    />
                    <Skeleton
                        variant="rounded"
                        height={40}
                        width="100%"
                        sx={{ borderRadius: 2 }}
                    />
                </Box>

                {/* Buttons */}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Skeleton variant="rounded" height={40} />
                    </Grid>
                    <Grid item xs={6}>
                        <Skeleton variant="rounded" height={40} />
                    </Grid>
                </Grid>
            </CardContent>

            {/* Footer */}
            <Box sx={{ p: 1, borderTop: "1px solid #eee", textAlign: "center" }}>
                <Skeleton variant="text" width="50%" height={20} sx={{ mx: "auto" }} />
            </Box>
        </Card>
    );
}

export default function PickYourCarGrid({ loader, data, endIconSrc, onDown, orderJet, costCalculate }) {
    const { title, subtitle, filters, jetData, type, options } = data;
    const [filter, setFilter] = React.useState("All");
    const [contact, setContact] = React.useState(INITIAL_STATE)
    const [openEnquire, setOpenEnquire] = React.useState(false)
    const handleOpenEnquire = () => setOpenEnquire(true)
    const handleCloseEnquire = () => setOpenEnquire(false)
    const [selectedVehicle, setSelectedVehicle] = React.useState('')
    const [vehicleInfo, setVehicleInfo] = React.useState([])
    const [expandedId, setExpandedId] = React.useState(null);
    const [search, setSearch] = React.useState("");
    const list = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        return (filter === "All" ? jetData : jetData.filter(c => c.segment === filter))
            .filter((c) => {
                if (!q) return true;
                // Build a simple haystack from common fields
                const hay = [
                    c.title,
                    c.name,
                    c.segment,
                    c.brand,
                    c.model,
                ].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(q);
            });
    }, [jetData, filter, search]);

    React.useEffect(() => { setExpandedId(null); }, [filter, search]);

    const HEADER_OFFSET = 88;
    const scrollToId = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
        window.scrollTo({ top: y, behavior: "smooth" });
    };

    return (
        <Box sx={{ py: { xs: 6, md: 4 } }}>
            <Container sx={{ maxWidth: "1320px !important" }}>
                {/* Heading */}
                <Heading title={title} subtitle={subtitle} />

                {/* Toggle */}
                {/* Responsive Toggle */}
                <Box
                    sx={{
                        // graceful horizontal scroll if there are many items
                        overflowX: "auto",
                        mx: { xs: -2, sm: 0 },            // bleed to edges on mobile
                        px: { xs: 2, sm: 0 },
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: { xs: "column-reverse", md: "row" }
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="center"
                        useFlexGap
                        flexWrap="wrap"                    // wrap on small screens
                        sx={{
                            gap: { xs: 1, sm: 1.25, md: 1.5 } // responsive spacing
                        }}
                    >
                        {
                            type === "jet" ?
                                null
                                :
                                filters?.map((t, index) => {
                                    const active = filter === t;
                                    return (
                                        <Chip
                                            key={index}
                                            clickable
                                            onClick={() => setFilter(t)}
                                            label={t}
                                            // keep your active look, but make sizes responsive
                                            sx={{
                                                ...pillSx(active),
                                                px: { xs: 1.25, sm: 1.5, md: 1.75 },
                                                height: { xs: 36, sm: 36, md: 36 },
                                                borderRadius: 999,
                                                // responsive label sizing
                                                "& .MuiChip-label": {
                                                    px: 0,
                                                    fontWeight: 600,
                                                    fontSize: { xs: 12, sm: 13, md: 14 },
                                                },
                                            }}
                                        />
                                    );
                                })
                        }

                    </Stack>
                    <Stack
                        direction="row"
                        justifyContent="center"
                        useFlexGap
                        flexWrap="wrap"                    // wrap on small screens
                        sx={{
                            gap: { xs: 1, sm: 1.25, md: 1.5 }, // responsive spacing
                            mt: { xs: 2, sm: 2, md: 0 },
                            mb: { xs: 2, sm: 4, md: 0 }
                        }}
                    >
                        {
                            type === "jet" ? <Button
                                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    borderRadius: 999,
                                    width: { xs: 160, sm: 180, md: 200 },
                                    height: { xs: 47, md: 47 },
                                    px: 2.25,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: "#0D8196",
                                    // gradient border trick
                                    border: "2px solid transparent",
                                    background:
                                        "linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #81D0D5, #0D8196) border-box",
                                    boxShadow: "0 1px 2px rgba(13,129,150,.12)",
                                    "&:hover": {
                                        background:
                                            "linear-gradient(#f9ffff, #f9ffff) padding-box, linear-gradient(135deg, #81D0D5, #0D8196) border-box",
                                    },
                                }}
                                onClick={orderJet}
                            >
                                Order Jet
                            </Button> :
                                <InputBase
                                    type="search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search.."
                                    endAdornment={
                                        search ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Clear search"
                                                    onClick={() => setSearch("")}
                                                >
                                                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: "#707379" }} />
                                        </InputAdornment>
                                    }
                                    sx={{
                                        width: { xs: 160, sm: 180, md: 200 },
                                        height: { xs: 47, md: 47 },
                                        px: 1,
                                        borderRadius: 999,
                                        bgcolor: "#fff",
                                        border: "1px solid #E5E7EB",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                    }}
                                />
                        }
                        <CtaButton label="Cost Calculator" onClick={costCalculate} />
                    </Stack>
                </Box>


                {/* Cards grid */}
                <Grid container spacing={4} sx={{ mt: 2 }} alignItems="flex-start">
                    {
                        loader ?
                            [...Array(6)].map((_, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <JetCardSkeleton />
                                </Grid>
                            ))
                            :
                            list.map((car, index) => (
                                <Grid key={index} item xs={12} sm={6} md={4}>
                                    <CarInvestmentCard
                                        {...car}
                                        openBreakdown={expandedId === car.id}
                                        onToggle={() =>
                                            setExpandedId(expandedId === car.id ? null : car.id)
                                        }
                                        FleetData={list}
                                        type={type}
                                        setSelectedVehicle={setSelectedVehicle}
                                        setVehicleInfo={setVehicleInfo}
                                        vehicleInfo={vehicleInfo}
                                        setContact={setContact}
                                        selectedVehicle={selectedVehicle}
                                        contact={contact}
                                        setOpenEnquire={setOpenEnquire}
                                    />
                                </Grid>
                            ))
                    }
                    {!loader && list.length === 0 && (
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: "center", color: "#6b7280", py: 4 }}>
                                {` No jet founded.`}
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                        <CtaButton label="Investment Breakdown" onClick={() => scrollToId(onDown)} endIconSrc={endIconSrc ?? undefined} />
                        {/* <CtaButton cta={{ label: "Investment Breakdown", href: onDown ? onDown : "#" }} endIconSrc={endIconSrc ?? undefined} /> */}
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

const pillSx = (active) => ({
    flexShrink: 0,
    fontWeight: 700,
    minWidth: "150px",
    borderRadius: "999px",
    px: { xs: 1.5, md: 2.5 },
    py: { xs: 1, md: 3 },
    fontSize: { xs: 14, md: 16 },
    background: active
        ? "linear-gradient(180deg, #75c4c5 0%, #0d8196 100%)"
        : "#f1f1f1",
    color: active ? "#fff" : "#000",
    "&:hover": {
        background: active
            ? "linear-gradient(180deg, #4bb6b4 0%, #249f9c 100%)"
            : "#f7f9f9",
    },
    "& .MuiChip-label": { px: 0, fontWeight: 700 },
});